"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import { BeUser } from "../types/user"

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  accessTokenExpiresAt: number | null
  refreshTokenExpiresAt: number | null
  user: BeUser | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  setTokens: (
    accessToken: string,
    refreshToken: string,
    accessTokenExpiresAt: number,
    refreshTokenExpiresAt: number
  ) => void
  setUser: (user: BeUser) => void
  logout: () => void
  isTokenExpired: () => boolean
  isRefreshTokenExpired: () => boolean
  checkExpiration: () => void
}

export const useAuthStore = create<AuthState>()(
  persist<AuthState, [], [], Partial<AuthState>>(
    (
      set: (partialState: Partial<AuthState>) => void,
      get: () => AuthState
    ) => ({
      accessToken: null,
      refreshToken: null,
      accessTokenExpiresAt: null,
      refreshTokenExpiresAt: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      setTokens: (
        accessToken: string,
        refreshToken: string,
        accessTokenExpiresAt: number,
        refreshTokenExpiresAt: number
      ) => {
        set({
          accessToken,
          refreshToken,
          accessTokenExpiresAt,
          refreshTokenExpiresAt,
          isAuthenticated: true,
        })
      },

      setUser: (user: BeUser) => {
        set({ user })
      },

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          accessTokenExpiresAt: null,
          refreshTokenExpiresAt: null,
          user: null,
          isAuthenticated: false,
        })
      },

      isTokenExpired: () => {
        const { accessTokenExpiresAt } = get()
        if (!accessTokenExpiresAt) return true

        // accessTokenExpiresAt is already a Unix timestamp in seconds
        // Convert current time to seconds and compare directly
        const nowInSeconds = Math.floor(Date.now() / 1000)
        return nowInSeconds >= accessTokenExpiresAt
      },

      isRefreshTokenExpired: () => {
        const { refreshTokenExpiresAt } = get()
        if (!refreshTokenExpiresAt) return true

        // refreshTokenExpiresAt is already a Unix timestamp in seconds
        // Convert current time to seconds and compare directly
        const nowInSeconds = Math.floor(Date.now() / 1000)
        return nowInSeconds >= refreshTokenExpiresAt
      },

      checkExpiration: () => {
        const { accessTokenExpiresAt } = get()
        if (accessTokenExpiresAt) {
          const nowInSeconds = Math.floor(Date.now() / 1000)
          if (nowInSeconds >= accessTokenExpiresAt) {
            get().logout()
          }
        }
      },
    }),
    {
      name: "be:auth-storage",
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state: AuthState) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        accessTokenExpiresAt: state.accessTokenExpiresAt,
        refreshTokenExpiresAt: state.refreshTokenExpiresAt,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.checkExpiration()
        }
      },
    }
  )
)
